import { getCampaignStats, getCampaignUpcs } from "@/api/client";
import type { CampaignRow, CampaignUpcRow } from "@/api/types";
import { BudgetMeter } from "@/components/BudgetMeter";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { HorizontalBarChart } from "@/components/HorizontalBarChart";
import { storeCampaignRedemptionColumns } from "@/components/redemptionColumns";
import { StatCard } from "@/components/StatCard";
import { TimeSeriesAreaChart } from "@/components/TimeSeriesAreaChart";
import {
  ChartSkeleton,
  ErrorState,
  TableSkeleton,
} from "@/components/states";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useDebouncedUrlFilter } from "@/hooks/useUrlFilters";
import {
  formatCompact,
  formatCurrency,
  formatDate,
  formatNumber,
} from "@/lib/format";

const campaignColumns: Column<CampaignRow>[] = [
  {
    header: "Campaign",
    cell: (r) => r.campaignName,
    sortValue: (r) => r.campaignName,
  },
  {
    header: "Type",
    cell: (r) => r.campaignType,
    sortValue: (r) => r.campaignType,
  },
  {
    header: "Runs",
    cell: (r) => `${formatDate(r.startDate)} – ${formatDate(r.endDate)}`,
    // ISO dates sort correctly as strings.
    sortValue: (r) => r.startDate,
    csvValue: (r) => `${r.startDate} to ${r.endDate}`,
  },
  {
    header: "Reward",
    align: "right",
    cell: (r) => `$${r.rewardValue.toFixed(2)}`,
    sortValue: (r) => r.rewardValue,
  },
  {
    header: "Redemptions",
    align: "right",
    cell: (r) => formatNumber(r.redemptions),
    sortValue: (r) => r.redemptions,
  },
  {
    header: "Spend",
    align: "right",
    cell: (r) => formatCurrency(r.campaignSpend),
    sortValue: (r) => r.campaignSpend,
  },
  {
    header: "Budget used",
    cell: (r) => <BudgetMeter spend={r.campaignSpend} budget={r.campaignBudget} />,
    // Sort by fraction of budget burned; campaigns without a budget last.
    sortValue: (r) =>
      r.campaignBudget ? r.campaignSpend / r.campaignBudget : null,
    // The meter doesn't translate to CSV; export the raw budget instead.
    csvValue: (r) => r.campaignBudget,
    csvHeader: "Budget",
  },
];

const upcColumns: Column<CampaignUpcRow>[] = [
  {
    header: "Campaign",
    cell: (r) => r.campaignName,
    sortValue: (r) => r.campaignName,
  },
  { header: "UPC", cell: (r) => r.upc ?? "—", sortValue: (r) => r.upc },
  {
    header: "Brand",
    cell: (r) => r.brandName ?? "—",
    sortValue: (r) => r.brandName,
  },
  {
    header: "Subcategory",
    cell: (r) => r.subcategoryName ?? "—",
    sortValue: (r) => r.subcategoryName,
  },
  { header: "DMA", cell: (r) => r.dma ?? "—", sortValue: (r) => r.dma },
];

function CampaignUpcsCard() {
  const state = useApiData(getCampaignUpcs);
  // Filtering is client-side, so the instant draft drives it and the URL
  // copy just trails behind for shareability.
  const [filter, setFilter] = useDebouncedUrlFilter("q");

  const rows =
    state.status === "ready"
      ? state.data.upcs.filter((r) => {
          const needle = filter.trim().toLowerCase();
          if (!needle) return true;
          return [r.campaignName, r.upc, r.brandName, r.subcategoryName, r.dma]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(needle));
        })
      : [];

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Campaign UPCs</CardTitle>
          <CardDescription>
            Products behind each recent campaign, with brand and market
          </CardDescription>
        </div>
        {state.status === "ready" ? (
          <CsvDownloadButton name="campaign-upcs" columns={upcColumns} rows={rows} />
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {state.status === "loading" ? <TableSkeleton rows={5} /> : null}
        {state.status === "error" ? (
          <ErrorState message={state.message} onRetry={state.reload} />
        ) : null}
        {state.status === "ready" ? (
          <>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by campaign, UPC, brand, subcategory, or DMA"
              aria-label="Filter campaign UPCs"
              className="h-9 w-full max-w-md rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            <DataTable
              columns={upcColumns}
              rows={rows}
              rowKey={(r, i) => `${r.campaignName}-${r.upc}-${r.dma}-${i}`}
              emptyMessage="No UPCs match the filter"
            />
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function CampaignStatsPage() {
  const state = useApiData(getCampaignStats);
  // The page shell renders immediately with shimmer placeholders, and the
  // UPCs card below fetches independently — its request runs in parallel
  // with this one instead of waiting for it.
  const data = state.status === "ready" ? state.data : null;

  const totalRedemptions = data?.campaigns.reduce((s, c) => s + c.redemptions, 0);
  const totalSpend = data?.campaigns.reduce((s, c) => s + c.campaignSpend, 0);

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">Campaign &amp; redemption statistics</h1>
        <p className="text-sm text-muted-foreground">
          Campaign impact and redemption activity across the platform,
          last 90 days.
        </p>
      </div>

      {state.status === "error" ? (
        <ErrorState message={state.message} onRetry={state.reload} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Recent campaigns"
              value={data && formatCompact(data.campaigns.length)}
            />
            <StatCard
              label="Redemptions"
              value={
                totalRedemptions === undefined
                  ? null
                  : formatCompact(totalRedemptions)
              }
            />
            <StatCard
              label="Campaign spend"
              value={
                totalSpend === undefined ? null : formatCurrency(totalSpend)
              }
            />
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Redemptions over time</CardTitle>
                <CardDescription>Daily redemptions</CardDescription>
              </CardHeader>
              <CardContent>
                {!data ? (
                  <ChartSkeleton height={260} />
                ) : (
                  <TimeSeriesAreaChart
                    data={data.redemptionsOverTime.map((d) => ({
                      day: d.day,
                      value: d.redemptions,
                    }))}
                    seriesName="Redemptions"
                  />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Redemptions by DMA</CardTitle>
                <CardDescription>Store redemptions by DMA</CardDescription>
              </CardHeader>
              <CardContent>
                {data ? (
                  <HorizontalBarChart
                    data={data.redemptionsByDma.map((r) => ({
                      name: r.dma,
                      value: r.redemptions,
                    }))}
                    seriesName="Redemptions"
                  />
                ) : (
                  <ChartSkeleton height={260} />
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Campaign overview</CardTitle>
                <CardDescription>
                  Every recent campaign with redemption counts and budget burn
                </CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="campaign-overview"
                  columns={campaignColumns}
                  rows={data.campaigns}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={campaignColumns}
                  rows={data.campaigns}
                  rowKey={(r, i) => `${r.campaignName}-${i}`}
                  maxHeight="32rem"
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <div className="space-y-1.5">
                <CardTitle>Redemptions by campaign and store</CardTitle>
                <CardDescription>
                  What each store earned from each campaign
                </CardDescription>
              </div>
              {data ? (
                <CsvDownloadButton
                  name="store-campaign-redemptions"
                  columns={storeCampaignRedemptionColumns}
                  rows={data.redemptionsByCampaignByStore}
                />
              ) : null}
            </CardHeader>
            <CardContent>
              {data ? (
                <DataTable
                  columns={storeCampaignRedemptionColumns}
                  rows={data.redemptionsByCampaignByStore}
                  rowKey={(r, i) => `${r.storeName}-${r.campaignName}-${i}`}
                />
              ) : (
                <TableSkeleton />
              )}
            </CardContent>
          </Card>
        </>
      )}

      <CampaignUpcsCard />
    </div>
  );
}
